import { type FunctionComponent } from "react";
import { DeleteOutlined, EditOutlined, TrophyOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button, Popover, Space, Tag, Typography } from "antd";

import { type ScheduledEnrollChallengeAutomation } from "@fitness-app/data-models/entities/AutomationTemplate";

import { formatSentTime } from "~/modules/Automation/utils/formatSentTime";
import { supabase } from "~/store/initializeStore";

interface OwnProps {
  item: ScheduledEnrollChallengeAutomation;
  onEdit: () => void;
  onRemove: () => void;
}

type Props = OwnProps;

const EnrollChallengeItem: FunctionComponent<Props> = ({ item, onEdit, onRemove }) => {
  const sentTimeLabel = formatSentTime(item.sentTime);
  const byTemplate = Boolean(item.challengeTemplateId);

  const targetQuery = useQuery({
    queryKey: ["automation-enroll-challenge-target", item.challengeTemplateId ?? item.challengeId],
    staleTime: 60_000,
    enabled: Boolean(item.challengeTemplateId ?? item.challengeId),
    queryFn: async () => {
      const { data, error } = byTemplate
        ? await supabase
            .from("challenge_template")
            .select("name")
            .eq("id", item.challengeTemplateId ?? "")
            .maybeSingle<{ name: string }>()
        : await supabase
            .from("challenge")
            .select("name")
            .eq("id", item.challengeId ?? "")
            .maybeSingle<{ name: string }>();

      if (error) {
        throw error;
      }

      return data?.name ?? null;
    },
  });

  const targetName = targetQuery.data ?? item.challengeTemplateId ?? item.challengeId ?? "—";

  return (
    <Popover
      title="Zapis do wyzwania"
      content={
        <Space direction="vertical" className="min-w-[220px] max-w-[320px]">
          <Space>
            <Typography.Text type="secondary">Nazwa:</Typography.Text>
            <Typography.Text>{item.name}</Typography.Text>
          </Space>
          <Space>
            <Typography.Text type="secondary">Tryb:</Typography.Text>
            <Typography.Text>
              {byTemplate ? "bieżąca edycja cyklicznego wyzwania" : "konkretne wyzwanie"}
            </Typography.Text>
          </Space>
          <Space align="start">
            <Typography.Text type="secondary">{byTemplate ? "Szablon:" : "Wyzwanie:"}</Typography.Text>
            <Typography.Text>{targetName}</Typography.Text>
          </Space>
          <Space>
            <Typography.Text type="secondary">Czas realizacji:</Typography.Text>
            <Typography.Text>{sentTimeLabel}</Typography.Text>
          </Space>
          <Space>
            <Button icon={<EditOutlined />} type="text" onClick={onEdit} />
            <Button danger icon={<DeleteOutlined />} type="text" onClick={onRemove} />
          </Space>
        </Space>
      }
    >
      <span className="cursor-pointer">
        <TrophyOutlined className="mr-2" />
        <span>{sentTimeLabel}</span>
        <Tag color="gold" className="ml-2">
          Wyzwanie
        </Tag>
        <div className="max-w-[140px] truncate text-xs text-gray-500">{item.name}</div>
      </span>
    </Popover>
  );
};

export default EnrollChallengeItem;
